import React, { useEffect, useState } from 'react';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { SearchInput } from '../../components/ui/Input';
import { Building2, Users, FolderOpen, MoreHorizontal, Plus, X, ExternalLink, Link as LinkIcon, Copy, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
const builders = [{
  id: 1,
  name: 'Excel Homes',
  slug: 'excel',
  status: 'Active',
  projects: 12,
  users: 8,
  buyers: 342,
  joinedDate: '2023-06-15'
}, {
  id: 2,
  name: 'Schell Brothers',
  slug: 'schell',
  status: 'Active',
  projects: 8,
  users: 12,
  buyers: 567,
  joinedDate: '2023-03-22'
}, {
  id: 3,
  name: 'Mattamy Homes',
  slug: 'mattamy',
  status: 'Active',
  projects: 15,
  users: 20,
  buyers: 892,
  joinedDate: '2022-11-10'
}, {
  id: 4,
  name: 'Landmark Homes',
  slug: 'landmark',
  status: 'Trial',
  projects: 2,
  users: 3,
  buyers: 45,
  joinedDate: '2024-01-10'
}, {
  id: 5,
  name: 'Starward Homes',
  slug: 'starward',
  status: 'Active',
  projects: 5,
  users: 6,
  buyers: 234,
  joinedDate: '2023-09-08'
}, {
  id: 6,
  name: 'Brookfield',
  slug: 'brookfield',
  status: 'Active',
  projects: 18,
  users: 25,
  buyers: 1245,
  joinedDate: '2022-08-20'
}];
export function BuildersPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddBuilderModal, setShowAddBuilderModal] = useState(false);
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);
  const [newBuilder, setNewBuilder] = useState({
    name: '',
    email: '',
    contactName: '',
    slug: ''
  });
  const filteredBuilders = builders.filter(builder => builder.name.toLowerCase().includes(searchQuery.toLowerCase()) || builder.slug.toLowerCase().includes(searchQuery.toLowerCase()));
  // Auto-generate slug from name
  const generateSlug = (name: string) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').substring(0, 20);
  };
  // ESC key handler
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showAddBuilderModal) {
        setShowAddBuilderModal(false);
        setNewBuilder({
          name: '',
          email: '',
          contactName: '',
          slug: ''
        });
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showAddBuilderModal]);
  const handleAddBuilder = () => {
    console.log('Adding new builder:', newBuilder);
    setShowAddBuilderModal(false);
    setNewBuilder({
      name: '',
      email: '',
      contactName: '',
      slug: ''
    });
  };
  const handleCopyLink = (slug: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const url = `${window.location.origin}/${slug}/dashboard`;
    navigator.clipboard.writeText(url);
    setCopiedSlug(slug);
    setTimeout(() => setCopiedSlug(null), 2000);
  };
  const handleOpenPortal = (slug: string, e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(`/${slug}/dashboard`, '_blank');
  };
  return <>
      <div className="space-y-6 max-w-7xl mx-auto" data-id="element-2666">
        <div className="flex items-center justify-between" data-id="element-2667">
          <div data-id="element-2668">
            <h1 className="text-2xl font-bold text-slate-900" data-id="element-2669">Builders</h1>
            <p className="text-slate-500" data-id="element-2670">
              Manage builder companies and their portal access
            </p>
          </div>
          <Button leftIcon={<Plus className="h-4 w-4" data-id="element-2672" />} onClick={() => setShowAddBuilderModal(true)} data-id="element-2671">
            Add New Builder
          </Button>
        </div>

        <Card noPadding data-id="element-2673">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-slate-50 rounded-t-xl" data-id="element-2674">
            <SearchInput className="max-w-sm bg-white" placeholder="Search builders or URL slugs..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-2675" />
            <div className="flex gap-2" data-id="element-2676">
              <Button variant="outline" size="sm" data-id="element-2677">
                Filter
              </Button>
              <Button variant="outline" size="sm" data-id="element-2678">
                Export
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto" data-id="element-2679">
            <table className="w-full text-left text-sm" data-id="element-2680">
              <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200" data-id="element-2681">
                <tr data-id="element-2682">
                  <th className="px-6 py-4" data-id="element-2683">Company Name</th>
                  <th className="px-6 py-4" data-id="element-2684">Portal URL</th>
                  <th className="px-6 py-4" data-id="element-2685">Status</th>
                  <th className="px-6 py-4" data-id="element-2686">Projects</th>
                  <th className="px-6 py-4" data-id="element-2687">Buyers</th>
                  <th className="px-6 py-4 text-right" data-id="element-2688">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-2689">
                {filteredBuilders.length === 0 ? <tr data-id="element-2690">
                    <td colSpan={6} className="px-6 py-12 text-center text-slate-500" data-id="element-2691">
                      No builders found matching "{searchQuery}"
                    </td>
                  </tr> : filteredBuilders.map(builder => <tr key={builder.id} className="hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => navigate(`/admin/builders/${builder.id}`)} data-id="element-2692">
                      <td className="px-6 py-4" data-id="element-2693">
                        <div className="flex items-center gap-3" data-id="element-2694">
                          <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center" data-id="element-2695">
                            <Building2 className="h-5 w-5 text-slate-600" data-id="element-2696" />
                          </div>
                          <span className="font-medium text-slate-900" data-id="element-2697">
                            {builder.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-2698">
                        <div className="flex items-center gap-2" data-id="element-2699">
                          <code className="px-2 py-1 bg-slate-100 rounded text-sm text-slate-700 font-mono" data-id="element-2700">
                            /{builder.slug}
                          </code>
                          <button onClick={e => handleCopyLink(builder.slug, e)} className="p-1 text-slate-400 hover:text-slate-600 transition-colors" title="Copy portal URL" data-id="element-2701">
                            {copiedSlug === builder.slug ? <Check className="h-4 w-4 text-green-500" data-id="element-2702" /> : <Copy className="h-4 w-4" data-id="element-2703" />}
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-2704">
                        <Badge variant={builder.status === 'Active' ? 'success' : builder.status === 'Trial' ? 'warning' : 'neutral'} data-id="element-2705">
                          {builder.status}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-slate-600" data-id="element-2706">
                        <div className="flex items-center gap-1" data-id="element-2707">
                          <FolderOpen className="h-4 w-4 text-slate-400" data-id="element-2708" />
                          {builder.projects}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-slate-600" data-id="element-2709">
                        {builder.buyers}
                      </td>
                      <td className="px-6 py-4 text-right" data-id="element-2710">
                        <div className="flex items-center justify-end gap-2" data-id="element-2711">
                          <Button variant="outline" size="sm" onClick={e => handleOpenPortal(builder.slug, e)} leftIcon={<ExternalLink className="h-3 w-3" data-id="element-2713" />} data-id="element-2712">
                            Open Portal
                          </Button>
                          <Button variant="ghost" size="sm" onClick={e => {
                      e.stopPropagation();
                      navigate(`/admin/builders/${builder.id}`);
                    }} data-id="element-2714">
                            Details
                          </Button>
                        </div>
                      </td>
                    </tr>)}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Add New Builder Modal */}
      {showAddBuilderModal && <div className="fixed top-0 left-0 right-0 bottom-0 bg-black/50 flex items-center justify-center z-50" data-id="element-2715">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" data-id="element-2716">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between" data-id="element-2717">
              <div data-id="element-2718">
                <h2 className="text-xl font-bold text-slate-900" data-id="element-2719">
                  Add New Builder
                </h2>
                <p className="text-sm text-slate-500 mt-1" data-id="element-2720">
                  Create a new builder company account
                </p>
              </div>
              <button onClick={() => setShowAddBuilderModal(false)} className="text-slate-400 hover:text-slate-600 transition-colors" data-id="element-2721">
                <X className="h-5 w-5" data-id="element-2722" />
              </button>
            </div>
            <div className="p-6 space-y-4" data-id="element-2723">
              <div data-id="element-2724">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-2725">
                  Company Name
                </label>
                <input type="text" value={newBuilder.name} onChange={e => {
              const name = e.target.value;
              setNewBuilder({
                ...newBuilder,
                name,
                slug: newBuilder.slug || generateSlug(name)
              });
            }} placeholder="e.g., Acme Homes" className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" autoFocus data-id="element-2726" />
              </div>
              <div data-id="element-2727">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-2728">
                  Portal URL Slug
                </label>
                <div className="flex items-center gap-2" data-id="element-2729">
                  <span className="text-slate-500" data-id="element-2730">/</span>
                  <input type="text" value={newBuilder.slug} onChange={e => setNewBuilder({
                ...newBuilder,
                slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')
              })} placeholder="acme-homes" className="flex-1 px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500 font-mono" data-id="element-2731" />
                </div>
                <p className="text-xs text-slate-500 mt-1" data-id="element-2732">
                  This will be the URL for their portal: /
                  {newBuilder.slug || 'slug'}/dashboard
                </p>
              </div>
              <div data-id="element-2733">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-2734">
                  Primary Contact Name
                </label>
                <input type="text" value={newBuilder.contactName} onChange={e => setNewBuilder({
              ...newBuilder,
              contactName: e.target.value
            })} placeholder="e.g., John Smith" className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" data-id="element-2735" />
              </div>
              <div data-id="element-2736">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-2737">
                  Contact Email
                </label>
                <input type="email" value={newBuilder.email} onChange={e => setNewBuilder({
              ...newBuilder,
              email: e.target.value
            })} placeholder="e.g., john@acmehomes.com" className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" data-id="element-2738" />
              </div>
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-2739">
                <p className="text-xs text-blue-800" data-id="element-2740">
                  An invitation email will be sent to the contact email with
                  setup instructions.
                </p>
              </div>
            </div>
            <div className="p-6 border-t border-slate-200 flex justify-end gap-3" data-id="element-2741">
              <Button variant="outline" onClick={() => setShowAddBuilderModal(false)} data-id="element-2742">
                Cancel
              </Button>
              <Button onClick={handleAddBuilder} disabled={!newBuilder.name || !newBuilder.email || !newBuilder.contactName || !newBuilder.slug} leftIcon={<Plus className="h-4 w-4" data-id="element-2744" />} data-id="element-2743">
                Add Builder
              </Button>
            </div>
          </div>
        </div>}
    </>;
}